'use client'
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import SearchBar from './SearchBar'
import MostSearched from './MostSearched'
import SuggestedResult from './SuggestedResult'
import QueryResult from './QueryResult'
import { useGetMostSearchedDataQuery, useGetRecentlyViewedProductsDataQuery, useGetSearchProductsDataQuery, useGetSearchSuggestionsDataQuery } from '@/src/store/rtkQueries/userGetAPI'
import { getCategoriesPath } from '@/src/routes/routes'
import SuggestedProductsSkeleton from '@/src/ui/skeletons/SuggestedProductsSkeleton'
import MostSearchedSkeleton from '@/src/ui/skeletons/MostSearchedSkeleton'
import ImageComponent from '../../common/ImageComponent'

interface SearchResultProps {
    isOpen: boolean
    onClose: () => void
    logoSrc: string
}

const SEARCH_DEBOUNCE_MS = 300

export default function SearchResult({ isOpen, onClose, logoSrc }: SearchResultProps) {
    const router = useRouter()
    const [query, setQuery] = useState('')
    const [debouncedQuery, setDebouncedQuery] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)
    const trimmedQuery = query.trim()
    const debouncedTrimmed = debouncedQuery.trim()
    const isSearchDebouncing = trimmedQuery !== debouncedTrimmed
    const { data: mostSearchedRes, isLoading: isMostSearchedLoading } = useGetMostSearchedDataQuery(undefined, { skip: !isOpen })
    const mostSearchedData = mostSearchedRes?.data ?? []

    const { data: res, isLoading: isRecentlyViewedLoading } = useGetRecentlyViewedProductsDataQuery(undefined, { skip: !isOpen })
    const suggestedProducts = res?.data ?? []

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedQuery(query)
        }, SEARCH_DEBOUNCE_MS)
        return () => window.clearTimeout(timer)
    }, [query])

    const {
        data: searchRes,
        isLoading: isSearchLoading,
        isFetching: isSearchFetching,
    } = useGetSearchProductsDataQuery(
        { search: debouncedTrimmed, page: 1, limit: 20 },
        { skip: !debouncedTrimmed }
    )
    const {
        data: suggestionsRes,
        isLoading: isSuggestionsLoading,
        isFetching: isSuggestionsFetching,
    } = useGetSearchSuggestionsDataQuery(
        { search: debouncedTrimmed, limit: 10 },
        { skip: !debouncedTrimmed }
    )

    const showSearchProductsSkeleton =
        isSearchLoading || isSearchFetching || isSearchDebouncing
    const showSearchSuggestionsSkeleton =
        isSuggestionsLoading || isSuggestionsFetching || isSearchDebouncing
    const searchProducts = searchRes?.data?.data ?? []
    const searchTotalCount = searchRes?.data?.pagination?.count
    const searchSuggestions = suggestionsRes?.data?.suggestions ?? []

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 50)
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
        }
        return () => { document.body.style.overflow = '' }
    }, [isOpen])

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose()
    }, [onClose])

    useEffect(() => {
        if (isOpen) document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [isOpen, handleKeyDown])

    const handleClear = () => {
        setQuery('')
        setDebouncedQuery('')
        inputRef.current?.focus()
    }

    const handleClose = () => {
        setQuery('')
        setDebouncedQuery('')
        onClose()
    }

    const goToShopSearch = useCallback(() => {
        if (!trimmedQuery) return
        router.push(`${getCategoriesPath()}?search=${trimmedQuery}`)
        setQuery('')
        setDebouncedQuery('')
        onClose()
    }, [trimmedQuery, router, onClose])

    return (
        <>
            <div className={`fixed inset-0 z-60 transition-all duration-300 ease-in-out ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
                <div className="absolute inset-0 bg-black/50" onClick={onClose} />

                <div className={`relative min-h-[calc(60vh)] mx-4 my-28 rounded-xl overflow-hidden bg-[#E7E6E6CC] border border-muted-color backdrop-blur-[5.45px] shadow-[0_8px_40px_0_rgba(0,0,0,0.12)] transition-transform duration-300 ease-in-out ${isOpen ? 'translate-y-0' : '-translate-y-20'}`}>
                    <div className="bg-[#E3E3E3] border-b border-muted-color">
                        <div className="absolute bottom-3 left-4 w-24 md:w-32 h-8 md:h-10 transition-[filter] duration-500 ease-in-out invert">
                            <ImageComponent src={logoSrc} alt="Logo" object_cover={false} priority={true} />
                        </div>
                        <SearchBar
                            query={query}
                            inputRef={inputRef}
                            onChange={setQuery}
                            onClear={handleClear}
                            onClose={handleClose}
                            onSubmitSearch={goToShopSearch}
                        />
                    </div>
                    <div className="p-4">
                        {trimmedQuery ? (
                            <QueryResult
                                query={query}
                                onClose={handleClose}
                                onSuggestionClick={setQuery}
                                searchSuggestions={searchSuggestions}
                                isSuggestionsLoading={showSearchSuggestionsSkeleton}
                                searchProducts={searchProducts}
                                searchTotalCount={searchTotalCount}
                                isSearchLoading={showSearchProductsSkeleton}
                            />
                        ) : (
                            (() => {
                                const hasRecentlyViewed = isRecentlyViewedLoading || suggestedProducts?.length > 0
                                return (
                                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                        <div className={`${hasRecentlyViewed ? 'col-span-3' : 'col-span-full'} shrink-0 space-y-5`}>
                                            {isMostSearchedLoading
                                                ? <MostSearchedSkeleton />
                                                : <MostSearched mostSearchedData={mostSearchedData} activeTag={query} onSelect={setQuery} />}
                                        </div>
                                        {hasRecentlyViewed && (
                                            <div className="col-span-2 space-y-5">
                                                {isRecentlyViewedLoading
                                                    ? <SuggestedProductsSkeleton count={4} />
                                                    : <SuggestedResult suggestedProducts={suggestedProducts} onClose={handleClose} />}
                                            </div>
                                        )}
                                    </div>
                                )
                            })()
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}
